import type { ColumnConfig, SalesHierarchyNode } from "../types"
import { build_monthly_data_from_points } from "./monthly_metrics_view"
import { parse_period_key_to_time_period, shift_week_period_key } from "./periods"

/**
 * Ship-to revenue by month: one row per ship-to (distributor) root of the Ship-to view, one
 * column per calendar month, dollars as the grid shows them.
 *
 * The roots come from `buildForecastTableHierarchy(…, ViewMode.SHIP_TO_VIEW, …)`, so the file
 * matches the Ship-to grid to the cent. A weekly plan rolls up to the month its ISO week's
 * Thursday falls in — the same month `parse_period_key_to_time_period` gives every other
 * month-indexed reader — so the columns add up to the weekly total.
 *
 * The plan is kept by consumption (depletion) week. With `ship_week_lead_time` set, each week's
 * dollars move that many weeks earlier before they are rolled into a month — the same shift the
 * grid's Ship weeks view labels its columns with — so the file reads as revenue by the month
 * product ships. The total is unchanged. A monthly plan has no weeks to move and is left as is.
 */

function month_key_for_period(period_key: string, ship_week_lead_time: number | null): string {
  const ship_key = ship_week_lead_time != null ? shift_week_period_key(period_key, -ship_week_lead_time) : null
  const period = parse_period_key_to_time_period(ship_key ?? period_key)
  return `${period.year}-${String(period.month).padStart(2, "0")}`
}

function csv_cell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}

function cents_to_dollars(cents: number): string {
  return (cents / 100).toFixed(2)
}

export interface ShipToMonthlyRevenueRow {
  ship_to: string
  /** Cents per `YYYY-MM`. */
  cents_by_month: Map<string, number>
}

export function ship_to_revenue_by_month(
  ship_to_roots: SalesHierarchyNode[],
  columnConfig: ColumnConfig,
  ship_week_lead_time: number | null = null
): ShipToMonthlyRevenueRow[] {
  return ship_to_roots.map((root: SalesHierarchyNode) => {
    const cents_by_month = new Map<string, number>()
    const by_period = build_monthly_data_from_points(root.sales_data, columnConfig)
    for (const [period_key, cell] of Object.entries(by_period)) {
      const month_key = month_key_for_period(period_key, ship_week_lead_time)
      const cents = Math.round(Number(cell.volume_dollars || 0) * 100)
      cents_by_month.set(month_key, (cents_by_month.get(month_key) ?? 0) + cents)
    }
    return { ship_to: root.name, cents_by_month }
  })
}

export function build_ship_to_revenue_by_month_csv(
  ship_to_roots: SalesHierarchyNode[],
  columnConfig: ColumnConfig,
  ship_week_lead_time: number | null = null
): string {
  const rows = ship_to_revenue_by_month(ship_to_roots, columnConfig, ship_week_lead_time)
  const month_keys = [
    ...new Set(rows.flatMap((row: ShipToMonthlyRevenueRow) => [...row.cents_by_month.keys()])),
  ].sort()

  const lines = [["Ship-To Customer", ...month_keys, "Total"].join(",")]
  for (const row of rows) {
    const month_cents = month_keys.map((key: string) => row.cents_by_month.get(key) ?? 0)
    const total_cents = month_cents.reduce((sum: number, cents: number) => sum + cents, 0)
    lines.push([csv_cell(row.ship_to), ...month_cents.map(cents_to_dollars), cents_to_dollars(total_cents)].join(","))
  }
  return lines.join("\n")
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  const column_config = {} as ColumnConfig

  const point = (period_key: string, dollars: number): any => ({
    period: { ...parse_period_key_to_time_period(period_key) },
    metrics: { volume_dollars: dollars, cases_sold: 0, store_count: 0, case_cost: 0 },
    is_forecast: true,
  })

  const root = (name: string, points: any[]): SalesHierarchyNode =>
    ({ id: name, name, level: "distributor", sales_data: points, children: [] }) as any

  describe("build_ship_to_revenue_by_month_csv", () => {
    it("rolls weeks into the month their Thursday falls in", () => {
      // 2026-W05 runs Jan 26 – Feb 1; its Thursday is Jan 29. 2026-W06's Thursday is Feb 5.
      const csv = build_ship_to_revenue_by_month_csv(
        [root("KeHE", [point("2026-W04", 100.1), point("2026-W05", 200.2), point("2026-W06", 50)])],
        column_config
      )
      expect(csv.split("\n")).toEqual(["Ship-To Customer,2026-01,2026-02,Total", "KeHE,300.30,50.00,350.30"])
    })

    it("keeps a monthly plan's months and fills a ship-to's missing months with zero", () => {
      const csv = build_ship_to_revenue_by_month_csv(
        [root("UNFI East", [point("2027-01", 10), point("2027-02", 20)]), root("Direct", [point("2027-02", 5)])],
        column_config
      )
      expect(csv.split("\n")).toEqual([
        "Ship-To Customer,2027-01,2027-02,Total",
        "UNFI East,10.00,20.00,30.00",
        "Direct,0.00,5.00,5.00",
      ])
    })

    it("files the last ISO week of a year under its own year's December", () => {
      // 2026-W53 runs Dec 28 2026 – Jan 3 2027; its Thursday is Dec 31 2026.
      const rows = ship_to_revenue_by_month([root("KeHE", [point("2026-W53", 7)])], column_config)
      expect([...rows[0].cents_by_month.entries()]).toEqual([["2026-12", 700]])
    })

    it("moves each week earlier by the ship lead time before rolling into months", () => {
      // 2026-W06 (Thu Feb 5) and 2026-W08 (Thu Feb 19) ship three weeks earlier, in W03 and
      // W05 — both January. 2026-W09 (Thu Feb 26) ships in W06, which is February.
      const points = [point("2026-W06", 100), point("2026-W08", 20), point("2026-W09", 3)]
      expect(build_ship_to_revenue_by_month_csv([root("KeHE", points)], column_config).split("\n")).toEqual([
        "Ship-To Customer,2026-02,Total",
        "KeHE,123.00,123.00",
      ])
      expect(build_ship_to_revenue_by_month_csv([root("KeHE", points)], column_config, 3).split("\n")).toEqual([
        "Ship-To Customer,2026-01,2026-02,Total",
        "KeHE,120.00,3.00,123.00",
      ])
    })

    it("ships the first weeks of a year in the December before", () => {
      // 2027-W01 runs Jan 4 – 10; three weeks earlier is 2026-W51, whose Thursday is Dec 17.
      const rows = ship_to_revenue_by_month([root("KeHE", [point("2027-W01", 9)])], column_config, 3)
      expect([...rows[0].cents_by_month.entries()]).toEqual([["2026-12", 900]])
    })

    it("leaves a monthly plan where it is when a lead time is given", () => {
      const rows = ship_to_revenue_by_month([root("KeHE", [point("2027-02", 5)])], column_config, 3)
      expect([...rows[0].cents_by_month.entries()]).toEqual([["2027-02", 500]])
    })

    it("quotes a ship-to name that holds a comma", () => {
      const csv = build_ship_to_revenue_by_month_csv([root('Chex, "West"', [point("2027-01", 1)])], column_config)
      expect(csv.split("\n")[1]).toBe('"Chex, ""West""",1.00,1.00')
    })
  })
}
